import { useEffect, useState, useReducer, useContext } from "react";
import { IDynamicFormCaseProps } from "./IDynamicFieldRenderer";
import { FormInput } from "../FormInputs/FormInputs";
import { FormSelect } from "../FormSelect";
import { FormCascade } from "../FormCascade";
import { FormDate } from "../FormDate";
import FormFileUpload from "../FormFileUpload";
import { Button, Col, Form } from "antd";
import FormTable from "../DynamicTable/FormTable";
import { FormRadioButtons } from "../FormRadioButtons";
import { FormCheckbox } from "../FormCheckbox";
import FormFieldSetComponent from "../FormFieldSet/FormFieldSet";
import FormInlineGrid from "../DynamicTable/FormInlineGrid";
import { API } from "../../Axios/Axios";
import { getCascadeFieldOptions } from "../../Helpers/APIEndpoints/Endpoints";
import dayjs from "dayjs";
import { useActionHandler } from "../ActionHandlerContext";
import { useAuthStore } from "../../AuthStore/AuthStore";
import * as AntIcons from "@ant-design/icons";
import { FormCheckboxGroup } from "../FormCheckboxGroup";
import { componentRegistry } from "../componentRegistry";
import StaticTable from "../../Components/DynamicTable/StaticTable";
import { LoaderContext } from "../../Helpers/Context/Context";
import { usePermission } from "../../Helpers/PermissionContext/PermissionContext";
import { pattern } from "../../Helpers/APICalls/Constants";
import EditableFieldSetComponent from "../FormFieldSet/EditableFieldSet";

const DynamicFormCase = ({
  field,
  fields = null,
  form,
  keyIdentity,
  handleButtonClick = () => {},
  checkButtonIsLoading = () => false,
  isReset = false,
  setIsReset = () => {},
  actionType = "",
  handleFieldSetButtonClick = () => {},
  dataSource,
  handleEditableFieldSetButtonClick = () => {},
  dataEditableSource,
  colsize,
  section = {},
  docName,
  mode,
  handleMaskValueChange = () => {},
  initialMaskValue,
  resetUpload,
  InterfaceId,
}: IDynamicFormCaseProps) => {
  const [staticTableData, setStaticTableData] = useState<any>();
  const { showLoader, hideLoader } = useContext(LoaderContext);
  const { HasPermission } = usePermission();

  useEffect(() => {
    if (field && field?.type?.toLowerCase() === "table") {
      (async () => {
        try {
          await showLoader();
          if (field?.datasource?.execData) {
            let functionName = field.datasource.execData ?? null;
            console.log(
              "typeof interfaceActionHandler[functionName]:",
              typeof interfaceActionHandler[functionName],
            );

            let autoSearchResult = await interfaceActionHandler[functionName](
              field.Id,
              form,
            );

            if (autoSearchResult && Array.isArray(autoSearchResult)) {
              const columnKeys = Object.keys(autoSearchResult[0]);
              const staticColumns = columnKeys
                .filter((key) => key.toLowerCase() !== "totalcount")
                .map((key) => ({
                  title: key,
                  dataIndex: key,
                  key: key,
                }));

              let actData = [];
              actData.push(field.fieldActionConfig);
              const actionConfigs = actData;
              setStaticTableData((prev) => ({
                ...prev,
                [field.name]: {
                  columns: staticColumns,
                  data: autoSearchResult,
                  actionConfig: actionConfigs,
                },
              }));
              forceUpdate();
            }
          }
        } catch (error) {
          console.error(`Failed to load data:`, error);
        } finally {
          await hideLoader();
        }
      })();
    }
  }, [field]);

  useEffect(() => {
    if (!field?.defaultOption) return;

    let value: any = null;

    if (field.defaultOption === "[me]") {
      value = commonContextParams?.userId?.toString(); // get from context/global as needed
    } else if (field.defaultOption === "[Today]") {
      value = dayjs();
    } else if (field.type.toLowerCase() === "select") {
      value = field.defaultOption.toString();
    } else {
      value = field.defaultOption
        .toString()
        .split(",")
        .map((val: any) => val.trim());
    }

    // Set it in the form
    form?.setFieldValue(field.name, value);
  }, [field?.defaultOption]);

  let colsizes = colsize ?? 6;

  const handleFieldChange = (name: string, value: any, key?: any) => {
    //NOSONAR: TODO Masking
    // if (name !== "Active") {
    //   handleMaskValueChange(name, value);
    // }
    // Ensure correct field name formatting
    const fieldName = key ? `${name}-${key}` : name;

    // Update form state
    form?.setFieldsValue({ [fieldName]: value });

    // Store selected values

    const currentField = fields?.find((f: { name: string }) => f.name === name);
    if (!currentField) return;

    // **Reset fields where the current field's ID is present in parentid**
    const dependentFields = fields
      ?.filter((f: { dependentFieldId: string }) =>
        f.dependentFieldId
          ?.split(",")
          .map(String)
          .includes(currentField.Id.toString()),
      )
      .map((f: { name: any }) => f.name);

    dependentFields?.forEach((field: any) => {
      form?.setFieldsValue({ [field]: [] });
    });
  };

  const parseDateValidationRules = (
    rulesFromApi: any[],
    getFieldValue: any,
    label: any,
  ) => {
    return rulesFromApi?.flatMap((rule) =>
      buildRuleSet(rule, getFieldValue, label),
    );
  };

  // ----------------- Helpers -----------------

  const buildRuleSet = (rule: any, getFieldValue: any, label: any) => {
    const rules: any[] = [];

    addRequiredRule(rule, label, rules);
    addMinMaxDateRule(rule, getFieldValue, rules);

    return rules;
  };

  const addRequiredRule = (rule: any, label: string, rules: any[]) => {
    if (rule.required) {
      rules.push({
        required: true,
        message: `${label} is required`,
      });
    }
  };

  const addMinMaxDateRule = (rule: any, getFieldValue: any, rules: any[]) => {
    if (!rule.minDate && !rule.maxDate) return;

    rules.push({
      validator: (_: any, value: any) =>
        validateDateRange(value, rule, getFieldValue),
    });
  };

  // ----------------- Validation Logic -----------------

  const validateDateRange = (value: any, rule: any, getFieldValue: any) => {
    if (!value) return Promise.resolve();

    const minDate = resolveDate(rule.minDate, getFieldValue);
    const maxDate = resolveDate(rule.maxDate, getFieldValue);

    if (minDate && dayjs(value).isBefore(minDate, "day")) {
      return Promise.reject(
          //new Error(`Date must be after ${minDate.format("YYYY-MM-DD")}`),
          new Error(`Date cannot be before ${minDate.format("YYYY-MM-DD")}`)
      );
    }

    if (maxDate && dayjs(value).isAfter(maxDate, "day")) {
      return Promise.reject(
          // new Error(`Date must be before ${maxDate.format("YYYY-MM-DD")}`),
          new Error(`Date cannot be after ${maxDate.format("YYYY-MM-DD")}`)
      );
    }

    return Promise.resolve();
  };

  // ----------------- Date Resolver -----------------

  const resolveDate = (rawDate: string | null, getFieldValue: any) => {
    if (!rawDate) return null;

    const match = parseDynamicDate(rawDate);
    if (match) return resolveDynamicKeyword(match, getFieldValue);

    return dayjs(rawDate);
  };

  const parseDynamicDate = (rawDate: string) => {
    const pattern = /^\[([A-Za-z]+)([+-]?\d*)\]$/;
    return pattern.exec(rawDate);
  };

  const resolveDynamicKeyword = (
    match: RegExpMatchArray,
    getFieldValue: any,
  ) => {
    const keyword = match[1];
    const offset = parseInt(match[2] || "0", 10);

    if (keyword === "Today") return dayjs().add(offset, "day");

    const fieldValue = getFieldValue(keyword);
    return fieldValue ? dayjs(fieldValue).add(offset, "day") : null;
  };

  // const parseInputValidationRules = (rulesFromApi: any[]) => {
  //   return rulesFromApi?.flatMap((rule) => {
  //     if (rule.pattern) {
  //       const matchingApiRule = rulesFromApi?.find(
  //         (r) => r.pattern === rule.pattern
  //       );
  //       if (matchingApiRule) {
  //         return {
  //           ...rule,
  //           message: "Invalid Value", // custom pattern message
  //         };
  //       }
  //     }

  //     return rule;
  //   });
  // };
  //const parseInputValidationRules = (rules: any[], enableSpecialChar: any) => {
  //  const patternRule = {
  //    pattern: pattern,
  //    message: "Special characters are not allowed",
  //  };

  //  if (enableSpecialChar === null) {
  //    return [...(Array.isArray(rules) ? rules : []), patternRule];
  //  }
  //  return Array.isArray(rules) ? rules : [];
    //};

    const parseInputValidationRules = (rules: any[], enableSpecialChar: any) => {
        const patternRule = {
            pattern: pattern,
            message: "Special characters are not allowed",
        };

        if (!Array.isArray(rules)) return [];

        const parsedRules = rules.flatMap((rule) => {
            const result: any[] = [];

            // REQUIRED
            if (rule.required === "true" || rule.required === true) {
                result.push({
                    required: true,
                    message: "This field is required",
                });
            }

            // MIN LENGTH
            if (rule.minLength) {
                result.push({
                    min: Number(rule.minLength),
                    message: `Minimum ${rule.minLength} characters required`,
                });
            }

            // MAX LENGTH
            if (rule.maxLength) {
                result.push({
                    max: Number(rule.maxLength),
                    message: `Maximum ${rule.maxLength} characters allowed`,
                });
            }

            // REGEX
            if (rule.regex) {
                const regexString = rule.regex
                    .replace(/^\/|\/$/g, "")
                    .replace(/\\\\/g, "\\"); // fix escaped JSON regex

                result.push({
                    pattern: new RegExp(regexString),
                    message: rule.customErrorMessage || "Invalid format",
                });
            }

            return result;
        });

        // SPECIAL CHARACTER RULE
        if (enableSpecialChar === null) {
            parsedRules.push(patternRule);
        }

        return parsedRules;
    };


  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const {
    interfaceFieldActionHandler,
    autoSearchActionHandler,
    interfaceActionHandler,
  } = useActionHandler();
  const { commonContextParams } = useAuthStore();

  const getExecParams = (data: any, execParams: any[]) => {
    let hasError = false;

    const params = execParams.reduce(
      (acc: Record<string, any>, param: { name: string }) => {
        const value = data[param.name] ?? "";

        if (value === "") {
          hasError = true;
          acc[param.name] = `${param.name} is required`;
        } else {
          acc[param.name] = value;
        }

        return acc;
      },
      {},
    );

    return { params, hasError };
  };

  const showExecParamErrors = (
    currentField: any,
    params: any,
    forceUpdate: any,
  ) => {
    currentField.fieldOptions = Object.values(params)
      .filter((msg) => typeof msg === "string" && msg.includes("is required"))
      .map((msg) => ({ label: msg as string, value: "" }));

    forceUpdate();
  };

  const normalizeExecParams = (params: any) => {
    Object.keys(params).forEach((key) => {
      const val = params[key];

      if (Array.isArray(val)) {
        params[key] = val.join(",");
      } else if (val?.$d) {
        params[key] = dayjs(val).format("YYYY-MM-DD HH:mm:ss");
      }
    });
  };

  const updateDropdownOptions = (
    currentField: any,
    response: any,
    name: string,
    form: any,
    forceUpdate: any,
  ) => {
    const list = response.data;
    currentField.fieldOptions = list.length
      ? list
      : [{ label: "No Data", value: "" }];

    if (!list.length) {
      form.setFieldsValue({ [name]: [] });
    }

    forceUpdate();
  };

  const handleClick = async (name: string) => {
    const currentField = fields.find((f: { name: string }) => f.name === name);
    if (!currentField?.lazyload) return;

    const data = form.getFieldsValue();

    // STEP 1: Validate execution parameters
    const { params, hasError } = getExecParams(
      data,
      currentField.execParams ?? [],
    );

    if (hasError) {
      showExecParamErrors(currentField, params, forceUpdate);
      return;
    }

    // STEP 2: Normalize parameters
    normalizeExecParams(params);

    // STEP 3: Fetch dropdown data
    try {
      const response = await fetchDropdownData(currentField.Id, params);

      if (Array.isArray(response.data)) {
        updateDropdownOptions(currentField, response, name, form, forceUpdate);
      }
    } catch (error) {
      console.error(`Failed to load data for ${name}:`, error);
    }
  };

  const handleSearch = async (
    e: any,
    name: string,
    autoCompleteFunction: string,
  ) => {
    const currentField = fields.find((f: { name: string }) => f.name === name);
    if (!currentField) return;

    let execParamsList = {};
    if (e.length >= 4) {
      execParamsList = { SearchString: e };

      try {
        let autoSearchResult = await autoSearchActionHandler[
          autoCompleteFunction
        ](currentField.Id, execParamsList, currentField.value);

        if (Array.isArray(autoSearchResult.data)) {
          currentField.fieldOptions = autoSearchResult.data.length
            ? autoSearchResult.data
            : [{ label: "No Data", value: "" }];

          forceUpdate();
        }
      } catch (error) {
        console.error(`Failed to load data for ${name}:`, error);
      }
    }
  };

  // Function to fetch dependent dropdown data
  const fetchDropdownData = async (fieldId: string, execParams: any[]) => {
    try {
      const response = await API.post(getCascadeFieldOptions, {
        FieldId: fieldId,
        InterfaceId,
        FormData: JSON.stringify(execParams),
      });

      if (response && !response.status) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.data; // Parse and return the JSON response
    } catch (error) {
      console.error(`Error fetching data for ${fieldId}:`, error);
      return []; // Return an empty array in case of an error
    }
  };

  useEffect(() => {
    if (field?.defaultValue) {
      // Dynamically setting default values after the component mounts
      handleFieldChange(field.name, field.defaultValue);
    }
  }, [form]);

  const getIcon = (iconName) => {
    const Icon = AntIcons[iconName];
    if (Icon) {
      return <Icon />;
    }
    // If icon name is not found, return null or a default icon (optional)
    return null;
  };

  let customRules;

  if (field.inputType === "Date") {
    customRules = parseDateValidationRules(
      field.rules,
      form.getFieldValue,
      field.name,
    );
  } else if (field.inputType?.toLowerCase() === "text") {
    customRules = parseInputValidationRules(
      field.rules,
      field.enableSpecialChar,
    );
  } else {
    customRules = field.rules;
  }

  const renderField = () => {
    // NOSONAR : ignore
    if (field?.isFieldVisible) {
      const renderFieldActionButton = (name) => {
        const { label, className, icon, actions } = field.fieldActionConfig;
        const handleFieldActionClick = async () => {
          const execData = actions?.[0]?.execData;
          const execType = actions?.[0]?.execType;
          let result;
          if (execType === "Function") {
            if (!interfaceFieldActionHandler[execData]) {
              console.error(`No handler found for function: ${label}`);
              return;
            }
            result = await interfaceFieldActionHandler[execData](
              name,
              initialMaskValue[name],
            );
            if (!result) {
              console.error(
                `Handler for "${label}" executed but returned no result.`,
              );
              return;
            } else {
              console.log(
                `Handler for "${label}" executed successfully: ${result}`,
              );
            }
          } else {
            console.error(`Unsupported execType: ${execData}`);
          }
        };

        return (
          <Button
            className={className}
            icon={getIcon(icon)}
            onClick={handleFieldActionClick}
            // style={{ height: "30px", marginTop: "5px" }}
          />
        );
      };
      switch (field?.type.toLowerCase()) {
        case "fieldset":
          return (
            <Col lg={24} xs={24} sm={24} md={12}>
              <FormFieldSetComponent
                // fields which are the child of the current fieldset,
                //  will be send to the form field set component
                fields={fields.filter(
                  (x: { parentId: any }) =>
                    x.parentId === field.fieldInterfaceId,
                )}
                form={form}
                handleFieldSetButtonClick={handleFieldSetButtonClick}
                dataSource={dataSource}
                fieldSetKey={field.name}
              />
            </Col>
          );

        case "editablefieldset":
          return (
            <Col lg={24} xs={24} sm={24} md={12}>
              <EditableFieldSetComponent
                // fields which are the child of the current fieldset,
                //  will be send to the form field set component
                fields={fields.filter(
                  (x: { parentId: any }) =>
                    x.parentId === field.fieldInterfaceId,
                )}
                form={form}
                handleFieldSetButtonClick={handleEditableFieldSetButtonClick}
                dataSource={dataEditableSource}
                fieldSetKey={field.name}
                InterfaceId={InterfaceId}
              />
            </Col>
          );

        case "input":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <div className="quickAdd d-flex gap-2">
                <FormInput
                  name={`${field.name}`}
                  label={field.label}
                  rules={customRules}
                  placeholder={field.placeholder}
                  disabled={field?.isFieldDisable}
                  widthSize={field.widthSize}
                  textArea={field.textArea}
                  inputType={field.inputType ?? "text"}
                  onChange={async (e: any) => {
                    handleFieldChange(
                      field.name,
                      field.inputType == "Text" ? e.target.value : e,
                    );
                    handleMaskValueChange(
                      field.name,
                      field.inputType == "Text" ? e.target.value : e,
                    );
                    if (field.onChange) {
                      await interfaceFieldActionHandler[field.onChange](form);
                    }
                  }}
                  onBlur={async (e: any) => {
                    console.log("onblur input");
                    if (field.onBlur) {
                      await interfaceFieldActionHandler[field.onBlur](form);
                    }
                  }}
                  value={field.value}
                />
                {field.fieldActionConfig && (
                  <div className="quick-group" style={{ paddingTop: "30px" }}>
                    {renderFieldActionButton(`${field.name}`)}
                  </div>
                )}
              </div>
            </Col>
          );

        case "textarea":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <FormInput
                name={`${field.name}`}
                label={field.label}
                rules={field.rules}
                placeholder={field.placeholder}
                disabled={field?.isFieldDisable}
                widthSize={field.widthSize}
                textArea={true}
                inputType={field.inputType ?? "text"}
                rows={field.rows}
                maxLength={field.maxLength}
                onChange={async (e: any) => {
                  handleFieldChange(field.name, e.target.value);
                  if (field.onChange) {
                    await interfaceFieldActionHandler[field.onChange](form);
                  }
                }}
                value={field.value}
              />
            </Col>
          );

        case "select":
        case "selectmulti":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <FormSelect
                name={`${field.name}`}
                label={field.label}
                rules={field.rules}
                placeholder={field.placeholder}
                disabled={field?.isFieldDisable}
                widthSize={field.widthSize}
                options={field.fieldOptions?.map(
                  (opt: { value: any; label: any }) => ({
                    value: String(opt.value),
                    label: opt.label,
                  }),
                )}
                filterOption={true}
                mode={
                  field.type.toLowerCase() === "selectmulti"
                    ? "multiple"
                    : undefined
                }
                onChange={async (value: any) => {
                  handleFieldChange(field.name, value, keyIdentity);
                  if (field.onChange) {
                    await interfaceFieldActionHandler[field.onChange](form);
                  }
                }}
                onSearch={(e: any) => {
                  if (field.autoComplete) {
                    handleSearch(e, field.name, field.autoComplete);
                  }
                }}
                onBlur={async (e: any) => {
                  console.log("onblur select");
                  if (field.onBlur) {
                    await interfaceFieldActionHandler[field.onBlur](form);
                  }
                }}
                onClick={() => handleClick(field.name)} // Trigger onClick
              />
            </Col>
          );

        case "cascade":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <FormCascade
                name={`${field.name}`}
                label={field.label}
                rules={field.rules}
                placeholder={field.placeholder}
                widthSize={field.widthSize}
                options={field.fieldOptions}
                disabled={field?.isFieldDisable}
                onChange={async (value: any) => {
                  handleFieldChange(field.name, value);
                  if (field.onChange) {
                    await interfaceFieldActionHandler[field.onChange](form);
                  }
                }}
              />
            </Col>
          );

        case "date":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <FormDate
                name={`${field.name}`}
                label={field.label}
                widthSize={field.widthSize}
                rules={customRules}
                format={field.format}
                disabled={field?.isFieldDisable}
                onChange={async (date: any) => {
                  handleFieldChange(field.name, date);
                  if (field.onChange) {
                    await interfaceFieldActionHandler[field.onChange](form);
                  }
                }}
                onBlur={async (e: any) => {
                  console.log("onblur date");
                  if (field.onBlur) {
                    await interfaceFieldActionHandler[field.onBlur](form);
                  }
                }}
                // disabled={field?.disabled}
                //defaultValue={field?.defaultValue}
                minDate={field?.minDate}
                maxDate={field?.maxDate}
              />
            </Col>
          );

        case "file":
        case "dragfile":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <FormFileUpload
                type={field.type}
                name={`${field.name}`}
                form={form}
                acceptedFileTypes={[field.acceptedFileTypes]}
                max={field.maxFileCount ?? 1}
                widthSize={field.widthSize}
                label={field.label}
                disabled={field?.isFieldDisable}
                rules={field.rules}
                filename={docName ? docName[field.name] : ""}
                resetUpload={resetUpload}
                maxFileSize={field.maxFileSize}
                totalMaxFilesSize={field.totalMaxFilesSize}
              />
            </Col>
          );

        case "checkbox":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <FormCheckbox
                name={`${field.name}`}
                label={field.label}
                required={field.rules?.some((rule: any) => rule.required)}
                disabled={field?.isFieldDisable}
                checked={
                  actionType === "Create"
                    ? field.defaultValue === "true"
                    : Form.useWatch(field.name, form) // NOSONAR : ignore
                }
                onChange={async (e: any) => {
                  handleFieldChange(field.name, e.target.checked);
                  if (field.onChange) {
                    await interfaceFieldActionHandler[field.onChange](form);
                  }
                }}
              />
            </Col>
          );

        case "checkboxmulti":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <FormCheckboxGroup
                name={`${field.name}`}
                label={field.label}
                required={field.rules?.some((rule: any) => rule.required)}
                options={field.fieldOptions?.map(
                  (opt: { value: any; label: any }) => ({
                    value: String(opt.value),
                    label: opt.label,
                  }),
                )}
                layout={field.layout}
                //defaultValue={field.defaultValue}
                onChange={(selectedValues: any[]) =>
                  handleFieldChange(field.name, selectedValues)
                }
              />
            </Col>
          );

        case "radio":
          return (
            <Col lg={colsizes} xs={24} sm={24} md={12}>
              <FormRadioButtons
                name={`${field.name}`}
                label={field.label}
                options={field.fieldOptions}
                disabled={field?.isFieldDisable}
                required={field.rules?.some((rule: any) => rule.required)}
                onChange={async (e: any) => {
                  handleFieldChange(field.name, e.target.value);
                  if (field.onChange) {
                    await interfaceFieldActionHandler[field.onChange](form);
                  }
                }}
              />
            </Col>
          );

        case "addrowtable":
          return (
            <FormTable
              columns={field.columns}
              isAddRowTable
              fields={[]}
              dataSource={field.dataSource}
              name={`${field.name}`}
              keyIdentity={keyIdentity}
              form={form}
              isReset={isReset}
              setIsReset={setIsReset}
              field={field}
              section={section}
            />
          );

        case "grid":
          return (
            <FormInlineGrid
              fields={fields.filter(
                (x: { parentId: any }) => x.parentId === field.fieldInterfaceId,
              )}
              name={`${field.name}`}
              keyIdentity={keyIdentity}
              form={form}
              field={field}
              section={section}
            />
          );

        case "table":
          return (
            <Col lg={24} xs={24} sm={24} md={24}>
              <StaticTable datasource={staticTableData[field?.name] ?? null} />
            </Col>
          );

        case "custom": {
          const DynamicComponent = componentRegistry[field.name];

          if (!DynamicComponent) {
            console.log(`Component "${field.label}" not found in registry.`);
            return false;
          }

          const dynamicProps = {
            name: `${field.name}`,
            form,
            onChange: async (value: any) => {
              handleFieldChange(field.name, value);
            },
            ...(field.props ? JSON.parse(field.props) : {}), // Additional props from backend
          };

          return (
            <>
              {/* // <Col lg={field.colsize ? 24 : colsizes} xs={24} sm={24} md={24}> */}

              {/* <Form.Item name={field.name} style={{ marginBottom: 0 }}> */}
              <DynamicComponent {...dynamicProps} />
              {/* </Form.Item> */}
              {/* // </Col> */}
            </>
          );
        }

        default:
          return null;
      }
    }
  };

  const actionKey = field.actionKey?.toLowerCase();
  const canEdit = HasPermission(field.visibility?.edit ?? []);
  const canReadOnly = HasPermission(field.visibility?.readOnly ?? []);

  return (
    <>
      {renderField()}
      {field.type.toLowerCase() === "button" && (canEdit || canReadOnly) && (
        <Button
          className={field.className}
          loading={checkButtonIsLoading(field.actionKey.toLowerCase())}
          disabled={
            (field.isFieldDisable
              ? true
              : canEdit
                ? false
                : canReadOnly
                  ? true
                  : false) ||
            (actionKey ? checkButtonIsLoading(actionKey) : false)
          }
          onClick={() => handleButtonClick(form, field)}
        >
          {
            checkButtonIsLoading(field.actionKey.toLowerCase())
              ? "Processing"
              : field.icon // NOSONAR : igonre
                ? getIcon(field.icon) // NOSONAR : igonre
                : field.label // NOSONAR : igonre
          }
        </Button>
      )}
    </>
  );
};

export default DynamicFormCase;
